import React, { useMemo, useCallback } from 'react'
import { Box, makeStyles } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { useQueryParam } from 'use-query-params'
import useAuthenStore from '../../../../../stores/authen/authenStore'
import useTranslate from '../../../../../i18n/useTranslate'
import submittedStatus from '../../../../../constants/submittedStatus'
import projectStatus from '../../../../../constants/projectStatus'
import { UserRole, SummaryItem } from './DashBoardType'
import StackedListCard, { StackedListItem as StackedItem } from './StackedListCard'
import NewSummaryCard from './NewSummaryCard'
import SummaryCard from './SummaryCard'
import PieChartCard from './PieChartCard'
import useDashboard from './useDashboard'

type QueryShape = {
    submittedStatus?: string[]
    submittedConsiderTypeId?: number
    submittedType?: string[]
    pendingAndRisk?: number
    progressOverdue?: number
    status?: number
    onlyCurrentYear?: number
}

const QS = {
    submittedStatus: 'submittedStatus',
    submittedConsiderTypeId: 'submittedConsiderTypeId',
    submittedType: 'submittedType',
    pendingAndRisk: 'pendingAndRisk',
    progressOverdue: 'progressOverdue',
    status: 'status',
    onlyCurrentYear: 'onlyCurrentYear',
} as const

const DEFAULT_SUBMITTED_STATUSES_STAFF: string[] = [
    String(submittedStatus.sent),
    String(submittedStatus.received),
    String(submittedStatus.waitSynthesis),
    String(submittedStatus.proceedingCertificate),
    String(submittedStatus.waitSign),
    String(submittedStatus.resultConsideration),
    String(submittedStatus.waitingSentReviewer),
]

const DEFAULT_SUBMITTED_STATUSES_BOARD: string[] = [
    String(submittedStatus.sent),
    String(submittedStatus.received),
    String(submittedStatus.waitSynthesis),
    String(submittedStatus.waitSign),
    String(submittedStatus.resultConsideration),
]

const getDefaultSubmittedStatuses = (role?: UserRole): string[] =>
    role === UserRole.Board ? DEFAULT_SUBMITTED_STATUSES_BOARD : DEFAULT_SUBMITTED_STATUSES_STAFF

const toQueryString = (params: QueryShape) => {
    const sp = new URLSearchParams()
    if (params.submittedStatus?.length) sp.set(QS.submittedStatus, params.submittedStatus.join(','))
    if (params.submittedConsiderTypeId != null)
        sp.set(QS.submittedConsiderTypeId, String(params.submittedConsiderTypeId))
    if (params.submittedType?.length) sp.set(QS.submittedType, params.submittedType.join(','))
    if (params.pendingAndRisk != null) sp.set(QS.pendingAndRisk, String(params.pendingAndRisk))
    if (params.progressOverdue != null) sp.set(QS.progressOverdue, String(params.progressOverdue))
    if (params.status != null) sp.set(QS.status, String(params.status))
    if (params.onlyCurrentYear != null) sp.set(QS.onlyCurrentYear, String(params.onlyCurrentYear))
    const q = sp.toString()
    return q ? `?${q}` : ''
}

const OVERVIEW_CLICKABLE_KEYS = new Set(['year', 'progress'])

const mapRole = (userGroupId: number | null): UserRole | undefined => {
    switch (userGroupId) {
        case 1:
            return UserRole.Researcher
        case 2:
            return UserRole.Staff
        case 5:
            return UserRole.Committee
        case 3:
        case 6:
        case 7:
            return UserRole.Board
        default:
            return undefined
    }
}

const Dashboard: React.FC = () => {
    const classes = useStyles()
    const profileStore = useAuthenStore()
    const translate = useTranslate()
    const history = useHistory()
    const [submittedStatusRaw] = useQueryParam<string | null>('submittedStatus')

    const role = mapRole(profileStore?.profile?.userGroupId)

    const statuses = useMemo<string[] | undefined>(() => {
        if (typeof submittedStatusRaw !== 'string') return undefined
        const ids = submittedStatusRaw
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        return ids.length ? ids : undefined
    }, [submittedStatusRaw])

    const { reviewStepList, submittedTypeList, projectTypeList, overviewList, pendingList, projectStatusList } =
        useDashboard(role, profileStore?.profile?.userGroupId ?? null, statuses)

    const sameSet = (a: string[] = [], b: string[] = []) => {
        if (a.length !== b.length) return false
        const aa = [...a].sort((x, y) => x.localeCompare(y))
        const bb = [...b].sort((x, y) => x.localeCompare(y))
        return aa.every((v, i) => v === bb[i])
    }

    const navigateToManageProject = useCallback(
        (patch: QueryShape, preserveSubmittedStatus = false) => {
            const shouldIncludeSubmittedStatus = preserveSubmittedStatus && role !== UserRole.Committee
            const DEFAULTS = getDefaultSubmittedStatuses(role)
            const base: QueryShape = shouldIncludeSubmittedStatus
                ? {
                    submittedStatus:
                        !statuses || statuses.length === 0
                            ? DEFAULTS
                            : statuses.length === 1
                            ? statuses
                            : sameSet(statuses, DEFAULTS)
                            ? statuses
                            : DEFAULTS,
                  }
                : {}
            const next: QueryShape = { ...base, ...patch }
            if (role === UserRole.Committee) delete next.submittedStatus
            history.push(`/manage-project${toQueryString(next)}`)
        },
        [history, statuses, role],
    )

    const isTh = useMemo(
        () => translate.languageStore.currentLanguage === translate.languageKey.th,
        [translate.languageStore.currentLanguage],
    )

    const normalize = (arr: any[] = []): StackedItem[] =>
        arr.map((it) => {
            const value = Number(it.value ?? 0)
            const overdue = Number(it.overdue ?? 0)
            const label = isTh ? it.nameTh || '' : it.nameEn || ''
            return { key: String(it.key ?? ''), label, value, total: value, overdue, color: it.color }
        })

    const reviewItems = useMemo(() => normalize(reviewStepList), [reviewStepList])
    const submittedTypeItems = useMemo(() => normalize(submittedTypeList), [submittedTypeList])
    const projectTypeItems = useMemo(() => normalize(projectTypeList), [projectTypeList])

    const handleOverviewClick = useCallback(
        (item: SummaryItem) => {
            switch (item.key) {
                case 'year':
                    navigateToManageProject({ onlyCurrentYear: 1 }, false)
                    break
                case 'progress':
                    navigateToManageProject({ status: projectStatus.processing, onlyCurrentYear: 1 }, false)
                    break
                default:
                    break
            }
        },
        [navigateToManageProject],
    )

    const handleListClick = useCallback(
        (
            type: 'submittedStatus' | 'submittedType' | 'submittedConsiderTypeId',
            item: StackedItem,
            onlyOverdue = false,
        ) => {
            switch (type) {
                case 'submittedStatus':
                    navigateToManageProject(
                        { 
                            status: 1, 
                            submittedStatus: [String(item.key)], 
                            progressOverdue: onlyOverdue ? 2 : undefined 
                        },
                        false,
                    )
                    break
                case 'submittedType':
                    navigateToManageProject(
                        { 
                            status: 1, 
                            submittedStatus: ['2','3','5','6','7','13','14','18'], 
                            submittedType: [String(item.key)], 
                            progressOverdue: onlyOverdue ? 2 : undefined 
                    },
                        true,
                    )
                    break
                case 'submittedConsiderTypeId':
                    navigateToManageProject(
                        {   status: 1, 
                            submittedStatus: ['2','3','5','6','7','13','14','18'], 
                            submittedConsiderTypeId: Number(item.key), 
                            progressOverdue: onlyOverdue ? 2 : undefined 
                        },
                        true,
                    )
                    break
            }
        },
        [navigateToManageProject],
    )

    return (
        <Box className={classes.root}>
            {(role === UserRole.Staff || role === UserRole.Board) && (
                <>
                    <Box className={classes.grid2} style={{ marginBottom: 16 }}>
                        <StackedListCard
                            title={translate.get('UM.PROJECT_DASHBOARD.REVIEW_STEPS')}
                            items={reviewItems}
                            onClickRow={(it) => handleListClick('submittedStatus', it, false)}
                            onClickOverdue={(it) => handleListClick('submittedStatus', it, true)}
                        />

                        <Box className={classes.stackCol}>
                            <StackedListCard
                                title={translate.get('UM.PROJECT_DASHBOARD.PROJECT_TYPES')}
                                items={projectTypeItems}
                                labelBg="#ffc1bf"
                                onClickRow={(it) => handleListClick('submittedConsiderTypeId', it, false)}
                                onClickOverdue={(it) => handleListClick('submittedConsiderTypeId', it, true)}
                            />

                            <StackedListCard
                                title={translate.get('UM.PROJECT_DASHBOARD.SUBMISSION_TYPES')}
                                items={submittedTypeItems}
                                labelBg="#999b19"
                                onClickRow={(it) => handleListClick('submittedType', it, false)}
                                onClickOverdue={(it) => handleListClick('submittedType', it, true)}
                            />
                        </Box>
                    </Box>

                    <Box className={classes.grid2}>
                        <NewSummaryCard
                            title={translate.get('UM.PROJECT_DASHBOARD.PROJECT_OVERVIEW')}
                            items={overviewList}
                            canClickItem={(it) => OVERVIEW_CLICKABLE_KEYS.has(it.key)}
                            onClickProcessingText={handleOverviewClick}
                            onClickOverdueText={handleOverviewClick}
                        />
                        <NewSummaryCard
                            title={translate.get('UM.PROJECT_DASHBOARD.PENDING_AND_HIGH_RISK')}
                            items={pendingList}
                            onClickLabel={(item) =>
                                navigateToManageProject({ pendingAndRisk: Number(item.key) }, false)
                            }
                            onClickProcessingText={(item) =>
                                navigateToManageProject({ pendingAndRisk: Number(item.key) }, false)
                            }
                            onClickOverdueText={(item) =>
                                navigateToManageProject({ pendingAndRisk: Number(item.key), progressOverdue: 2 }, false)
                            }
                        />
                    </Box>
                </>
            )}

            {role === UserRole.Committee && (
                <Box className={classes.grid2}>
                    <PieChartCard
                        title={translate.get('UM.PROJECT_DASHBOARD.SUBMISSION_TYPES')}
                        steps={submittedTypeList}
                        columns={1}
                        onClickLabel={(item) => navigateToManageProject({ submittedType: [String(item.key)] }, true)}
                        onClickProcessingText={(item) =>
                            navigateToManageProject({ submittedType: [String(item.key)] }, true)
                        }
                        onClickOverdueText={(item) =>
                            navigateToManageProject({ submittedType: [String(item.key)], progressOverdue: 2 }, true)
                        }
                    />
                    <PieChartCard
                        title={translate.get('UM.PROJECT_DASHBOARD.PROJECT_TYPES')}
                        steps={projectTypeList}
                        columns={1}
                        onClickLabel={(item) =>
                            navigateToManageProject({ submittedConsiderTypeId: Number(item.key) }, true)
                        }
                        onClickProcessingText={(item) =>
                            navigateToManageProject({ submittedConsiderTypeId: Number(item.key) }, true)
                        }
                        onClickOverdueText={(item) =>
                            navigateToManageProject(
                                { submittedConsiderTypeId: Number(item.key), progressOverdue: 2 },
                                true,
                            )
                        }
                    />
                </Box>
            )}

            {role === UserRole.Researcher && (
                <Box className={classes.grid2}>
                    <SummaryCard
                        title={translate.get('UM.PROJECT_DASHBOARD.PROJECT_OVERVIEW')}
                        items={overviewList}
                        canFilter={false}
                    />

                    <PieChartCard
                        title={translate.get('UM.PROJECT_DASHBOARD.PROJECT_STATUS')}
                        steps={projectStatusList}
                        canFilter={false}
                        columns={1}
                    />
                </Box>
            )}
        </Box>
    )
}

const useStyles = makeStyles((theme) => ({
    root: { display: 'flex', flexDirection: 'column', width: '100%' },

    grid2: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 16,
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
        },
    },

    stackCol: {
        display: 'grid',
        gridAutoRows: 'min-content',
        rowGap: 16,
    },
}))

export default Dashboard
